import React, { FC } from 'react';
import { Select } from '@ui';

import { useFieldArray, useFormContext } from 'react-hook-form';

import { OptionItem } from '@app/components/ui/select/select.type';

import Icon from '@app/components/ui/icons';

import useTranslation from '@app/hooks/use-translation';

import { KeysForFilteringType } from '@app/components/ui/easy-filter/types';

import { UniversalFilterConstants } from '@app/hooks/use-filter-data';

import NestedField from './nested-field';
type FilterFieldType = {
  keysForFiltering: KeysForFilteringType[];
  modalMode?: boolean;
};
const Field: FC<FilterFieldType> = (props) => {
  const { keysForFiltering } = props;
  const { t } = useTranslation('components.filter');

  const { control, setValue, watch } = useFormContext();
  const {
    fields,
    append: appendGroup,
    remove: removeGroup,
  } = useFieldArray({
    control,
    name: 'filter',
  });

  return (
    <ul className="flex flex-col">
      {fields.map((field, groupIndex) => {
        return (
          <li key={field.id} className="relative">
            {groupIndex > 0 && (
              <div
                className={
                  'h-[17px] w-full flex items-center justify-center text-[14px] leading-[17px] font-[400] text-4color'
                }
              >
                {t('and')}
              </div>
            )}
            <GroupItem
              keysForFiltering={keysForFiltering}
              control={control}
              groupIndex={groupIndex}
              setValue={setValue}
              watch={watch}
              appendGroup={appendGroup}
              groupLength={fields.length}
            />
            {fields.length !== groupIndex + 1 && (
              <div
                className={`cursor-pointer absolute -right-[25px] -translate-y-1/2  ${
                  groupIndex > 0 ? 'top-[calc(50%+9px)]' : 'top-[50%]'
                } `}
              >
                {/* right-[-25px] */}
                <Icon
                  name="TrashIcon"
                  className="!text-basic_red"
                  size={14}
                  onClick={() => removeGroup(groupIndex)}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default Field;

function GroupItem({
  control,
  groupIndex,
  watch,
  setValue,
  keysForFiltering,
  appendGroup,
  groupLength,
}) {
  const {
    fields,
    append: appendFieldsInsideGroup,
    remove: removeFieldsInsideGroup,
  } = useFieldArray({
    control,
    name: `filter.${groupIndex}`,
  });
  const { t } = useTranslation('components.filter');

  const { conditionsWithoutValue, conditions } = UniversalFilterConstants();
  function getParamsSelectOptions(): OptionItem[] {
    return keysForFiltering.map((item) => ({ title: item.title, value: item.id }));
  }

  function getConditionsSelectOptions(value?: string) {
    const alias = keysForFiltering.find((alias) => alias.id === value);
    if (value && alias && alias.type_meta) {
      return conditions
        .filter((condition) => !condition.metricOnly)
        .filter((condition) => condition.type_meta.includes(alias?.type_meta as string))
        .filter((condition) => alias.type_value && condition.values.includes(alias.type_value))
        .map((item) => ({ title: item.name, value: item.id }));
    } else
      return conditions
        .filter((condition) => !condition.aliasOnly)
        .filter((condition) => alias?.type_value && condition.values.includes(alias.type_value))
        .map((item) => ({ title: item.name, value: item.id }));
  }

  function getConditionOptionsWhenMoreThatOneValue() {
    return conditions
      .filter((condition) => condition.id === '=' || condition.id === '!=')
      .map((item) => ({ title: item.name, value: item.id }));
  }

  function onChangeParams(value, index) {
    setValue(`filter.${groupIndex}.${index}.id`, value);
    watch(`filter.${groupIndex}.${index}.value`)?.length > 1
      ? setValue(
          `filter.${groupIndex}.${index}.condition`,
          getConditionOptionsWhenMoreThatOneValue()[0].value,
        )
      : setValue(
          `filter.${groupIndex}.${index}.condition`,
          getConditionsSelectOptions(value)[0].value,
        );
  }

  return (
    <div className="py-4 flex flex-col relative bg-defaultBg rounded-[12px] border-solid border-l-[2px] border-speech_analitics px-[24px]">
      {fields.map((item, index) => (
        <React.Fragment key={item.id}>
          <div className={'flex items-start justify-center gap-[16px]'}>
            <div className={'w-[360px] relative'}>
              <div
                className={
                  'absolute w-[24px] h-[24px] left-[3px] z-[99] cursor-pointer flex items-center justify-center  -translate-y-1/2 top-[50%]'
                }
                onClick={() => {
                  if (index !== fields.length - 1) {
                    removeFieldsInsideGroup(index);
                    return;
                  }
                  appendFieldsInsideGroup({ id: '', condition: '', value: [''] });
                }}
              >
                <Icon
                  name={index !== fields.length - 1 ? 'MinusIcon' : 'CirclePlusIcon'}
                  size={14}
                  className="text-action"
                />
              </div>
              <Select
                customValueDisplayStyles={{ paddingLeft: '15px' }}
                dropWidth={356}
                defaultValue={watch(`filter.${groupIndex}.${index}.id`)}
                onChange={(value) => onChangeParams(value, index)}
                options={getParamsSelectOptions()}
                withSearch
                dropTop={index >= 2}
              />
            </div>
            <div className={'w-[120px]'}>
              <Select
                defaultValue={watch(`filter.${groupIndex}.${index}.condition`)}
                onChange={(value) => {
                  setValue(`filter.${groupIndex}.${index}.condition`, value);
                  if (
                    conditionsWithoutValue.includes(value.toString()) &&
                    fields.length < index + 2 &&
                    groupLength === groupIndex + 1
                  ) {
                    appendGroup([[{ id: '', condition: '', value: [''] }]]);
                  }
                }}
                dropTop={index >= 2}
                options={
                  watch(`filter.${groupIndex}.${index}.value`)?.length > 1
                    ? getConditionOptionsWhenMoreThatOneValue()
                    : getConditionsSelectOptions(watch(`filter.${groupIndex}.${index}.id`))
                }
              />
            </div>
            <NestedField
              getConditionOptionsWhenMoreThatOneValue={getConditionOptionsWhenMoreThatOneValue}
              getConditionsSelectOptions={getConditionsSelectOptions}
              fieldsLength={groupLength}
              groupIndex={groupIndex}
              appendGroup={appendGroup}
              nestIndex={index}
              keysForFiltering={keysForFiltering}
            />
          </div>
          {fields.length > 0 && index !== fields.length - 1 && (
            <div
              className={
                'h-[17px] w-full flex items-center justify-center text-[14px] leading-[17px] font-[400] text-4color'
              }
            >
              {t('or')}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
